import {
  PAYMENT_CANCELLATION_REASON,
  PENDING_PAYMENT_REASON,
  WITHDRAWAL_REASON_CANCELLED_WITHDRAWAL,
  WITHDRAWAL_REASON_PAYMENT_CANCELLED,
  WITHDRAWAL_REASON_PAYMENT_REVERSAL,
  WITHDRAWAL_REASON_WITHDRAWAL_REVERSAL,
} from "@/constants/ledgerReasons";

/**
 * A cancelled transaction books two legs against one `uuid`: the debit that
 * takes the money back out (the "Cancelled" badge), and the credit that returns
 * it (the "Returned" badge). They resolve to the same upstream record, so they
 * share a `createTime` and no ordering rule based on time or backend array
 * position can separate them deterministically.
 *
 * The return is the later effect of the cancellation, so each list orders the
 * pair the way it orders time:
 *
 * - newest-first lists (the on-screen activity list) put Returned above
 *   Cancelled — `compareReturnedBeforeCancelled`;
 * - oldest-first lists (the CSV/PDF statement) put Cancelled above Returned —
 *   `compareCancelledBeforeReturned`.
 *
 * Both read the same transaction in the same chronological sense; they are
 * exact mirror images of one another.
 */

/** Reasons whose leg renders the "Cancelled" badge (the debit). */
const CANCELLED_LEG_REASONS: ReadonlySet<string> = new Set([
  WITHDRAWAL_REASON_PAYMENT_CANCELLED,
  WITHDRAWAL_REASON_CANCELLED_WITHDRAWAL,
  // Legacy payments vocabulary: a `pending-payment` leg reads as "Cancelled"
  // once its `cancellation` counterpart exists under the same uuid.
  PENDING_PAYMENT_REASON,
]);

/** Reasons whose leg renders the "Returned" badge (the credit). */
const RETURNED_LEG_REASONS: ReadonlySet<string> = new Set([
  WITHDRAWAL_REASON_PAYMENT_REVERSAL,
  WITHDRAWAL_REASON_WITHDRAWAL_REVERSAL,
  PAYMENT_CANCELLATION_REASON,
]);

export interface CancellationPairEntry {
  uuid?: string;
  reason?: string;
}

/**
 * Chronological (oldest-first) comparator: orders the cancelled leg of a
 * transaction before its returned leg. Returns 0 for anything else, so it
 * composes as a tie-break. Used by the statement export.
 *
 * Deliberately keyed off `reason` rather than the sign of the balance: fee legs
 * share a uuid with the payment they belong to, and fx-ticket legs share a uuid
 * under their own ordering rule, so a blanket "debit before credit" would
 * disturb both.
 */
export function compareCancelledBeforeReturned(
  a: CancellationPairEntry,
  b: CancellationPairEntry
): number {
  // Legs of different transactions are ordered by the caller's other rules.
  if (!a.uuid || !b.uuid || a.uuid !== b.uuid) return 0;
  if (!a.reason || !b.reason) return 0;

  if (
    CANCELLED_LEG_REASONS.has(a.reason) &&
    RETURNED_LEG_REASONS.has(b.reason)
  ) {
    return -1;
  }
  if (
    RETURNED_LEG_REASONS.has(a.reason) &&
    CANCELLED_LEG_REASONS.has(b.reason)
  ) {
    return 1;
  }

  return 0;
}

/**
 * Reverse-chronological (newest-first) comparator: orders the returned leg of a
 * transaction above its cancelled leg, because the return is the later effect.
 * The exact mirror of `compareCancelledBeforeReturned`, with the same 0 for
 * every other pair (fee legs, fx legs, legs of different transactions). Used by
 * the on-screen activity list.
 */
export function compareReturnedBeforeCancelled(
  a: CancellationPairEntry,
  b: CancellationPairEntry
): number {
  return compareCancelledBeforeReturned(b, a);
}
